import { Link } from 'react-router-dom';
import { ScorekeeperLogo } from '../components/ScorekeeperLogo';
import { paths } from '../lib/paths';

const DEMO_PATH = `${paths.contact}?intent=book_demo`;

const EXPERIENCE_AUDIENCES = [
  {
    label: 'Operators',
    body: 'A cleaner way to keep the league moving without making the admin work the story.',
  },
  {
    label: 'Captains',
    body: 'Schedules, standings, roster context, and updates stay connected to the season.',
  },
  {
    label: 'Players',
    body: 'The league feels easier to follow, easier to trust, and more worth checking.',
  },
  {
    label: 'Fans',
    body: 'Scores, standings, results, and season momentum stay visible beyond the rink.',
  },
];

const HOME_SIGNAL = [
  'Registration',
  'Schedules',
  'Standings',
  'Stats',
  'Game results',
  'League updates',
  'Season story',
];

export function MarketingHomePage() {
  return (
    <>
      <section className="m-hero-center m-brand-hero">
        <ScorekeeperLogo lockup="primary" className="m-hero-center__logo" />
        <h1 className="sr-only">The Scorekeeper</h1>
        <p className="m-brand-hero__tagline" role="doc-subtitle">
          Intelligent league management, built so you can enjoy the game.
        </p>
        <p className="m-hero-center__lede">
          A modern roller hockey operating system that connects league operations,
          game day, communication, and the season experience in one place.
        </p>
        <div className="m-hero-center__cta">
          <Link to={DEMO_PATH} className="btn btn--primary">Book a Demo</Link>
          <a href="#platform" className="btn">Explore the Platform</a>
        </div>
      </section>

      <section id="platform" className="m-os-section m-os-section--soft m-anchor-section">
        <div className="m-os-section__inner">
          <div className="m-story-grid">
            <div>
              <p className="section-marker">Platform</p>
              <h2 className="display-headline display-headline--lg">
                Intelligent league management for roller hockey.
              </h2>
            </div>
            <div className="m-story-copy">
              <p>
                The Scorekeeper brings registration, schedules, communication,
                scorekeeping, standings, and league activity into one connected
                place built around how roller hockey actually runs.
              </p>
              <p>
                Operators get a clearer command center, and the league gets a
                more professional, connected experience from the first signup to
                the final result.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="game-day" className="m-os-section m-anchor-section">
        <div className="m-os-section__inner">
          <header className="m-os-section__head">
            <p className="section-marker">Game Day</p>
            <h2 className="display-headline display-headline--lg">
              Built for the rhythm of the rink.
            </h2>
            <p className="m-section-lede">
              From the schedule to puck drop, live scoring, final results, and
              updated standings, The Scorekeeper helps game day move with the
              pace of the rink.
            </p>
          </header>
          <div className="m-rink-panel" aria-label="Game day flow">
            <div className="m-rink-panel__rail">
              <span>Puck drop</span>
              <span>Live scoring</span>
              <span>Standings move</span>
              <span>Final result</span>
            </div>
            <div className="m-rink-panel__body">
              <p>Every game becomes part of a smarter season.</p>
              <span>Fast games. Tight windows. Clear results. Connected league momentum.</span>
            </div>
          </div>
        </div>
      </section>

      <section id="intelligence" className="m-os-section m-os-section--soft m-anchor-section">
        <div className="m-os-section__inner">
          <div className="m-story-grid m-story-grid--reverse">
            <div className="m-intel-card">
              <span>League intelligence built in</span>
              <strong>Results, stats, standings, and season activity become insight.</strong>
            </div>
            <div>
              <p className="section-marker">Intelligence</p>
              <h2 className="display-headline display-headline--lg">
                Make the season easier to understand and more interesting to follow.
              </h2>
              <div className="m-story-copy">
                <p>
                  League intelligence comes from the details already happening:
                  games played, results posted, standings moving, teams forming,
                  and people staying connected through the season.
                </p>
                <p>
                  The Scorekeeper turns that activity into useful context so the
                  league is easier to understand, manage, and follow.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="experience" className="m-os-section m-anchor-section">
        <div className="m-os-section__inner">
          <header className="m-os-section__head">
            <p className="section-marker">Experience</p>
            <h2 className="display-headline display-headline--lg">
              A better way to run, follow, and experience roller hockey.
            </h2>
            <p className="m-section-lede">
              League operators get cleaner control, while captains, players,
              teams, and fans get a better way to follow the season and stay
              connected to the league.
            </p>
          </header>
          <ul className="feature-grid" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {EXPERIENCE_AUDIENCES.map((item, index) => (
              <li key={item.label} className="feature-card">
                <div className="feature-card__head">
                  <span className="feature-card__tag">{item.label}</span>
                  <span className="feature-card__num">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="feature-card__title">{item.label}</h3>
                <p className="feature-card__body">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="m-os-section m-os-section--soft">
        <div className="m-os-section__inner">
          <div className="m-digital-home">
            <div>
              <p className="section-marker">Digital Home</p>
              <h2 className="display-headline display-headline--lg">
                The modern home for roller hockey leagues.
              </h2>
              <p className="m-section-lede">
                Every league needs a place where the season lives. The
                Scorekeeper becomes the home for registration, schedules,
                standings, stats, game results, league updates, and the story of
                the season.
              </p>
            </div>
            <ul className="stack-row m-home-signal" aria-label="League home signals">
              {HOME_SIGNAL.map((label) => (
                <li key={label} className="stack-card">
                  <span className="stack-card__label">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="m-os-section">
        <div className="m-os-section__inner">
          <div className="m-story-grid">
            <div>
              <p className="section-marker">Built for Roller Hockey</p>
              <h2 className="display-headline display-headline--lg">
                Designed for the full roller hockey experience.
              </h2>
            </div>
            <div className="m-story-copy">
              <p>
                Built around the pace, structure, and culture of roller hockey:
                fast games, tight rink windows, competitive divisions, captains,
                refs, scorekeepers, stats, standings, tournaments, and game-day
                momentum.
              </p>
              <p>
                For adult leagues, youth leagues, competitive leagues,
                recreational leagues, tournaments, rink-run programs, teams,
                players, captains, operators, and organized roller hockey
                communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="m-os-section m-os-section--soft m-anchor-section">
        <div className="m-os-section__inner">
          <div className="m-pricing-panel">
            <div>
              <p className="section-marker">Pricing</p>
              <h2 className="display-headline display-headline--md">
                Built around your league.
              </h2>
              <p className="m-section-lede">
                Pricing is custom for v1 because every league runs a little
                differently. We will learn how your season, tournaments,
                registrations, and game-day workflow operate, then shape the
                right setup.
              </p>
            </div>
            <Link to={DEMO_PATH} className="btn btn--primary">Book a Demo</Link>
          </div>
        </div>
      </section>

      <section className="m-os-cta">
        <p className="section-marker">Enjoy the Game</p>
        <h2 className="display-headline display-headline--lg">
          Enjoy the game. The Scorekeeper handles the league.
        </h2>
        <p className="m-os-cta__lede">
          Bring game day, league operations, experience, and intelligence into
          one modern platform built for roller hockey.
        </p>
        <div className="m-os-cta__buttons">
          <Link to={DEMO_PATH} className="btn btn--primary">Book a Demo</Link>
          <a href="#platform" className="btn">Explore the Platform</a>
        </div>
      </section>
    </>
  );
}
